import 'mapbox-gl/dist/mapbox-gl.css';
import { ReactElement, useEffect, useRef, useState } from 'react';
import Map from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import styles from './dashboard-map.module.scss';
import { MapUserLocation, UserMapMarker } from './user-map-marker';
import SuperCluster from 'supercluster';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import { MapButton } from './map-button';
import {
    getLocaleRegionBoundingBox,
    increaseBoundsBy,
    locationsToBounds
} from '../util/map-helpers';
import { MAPBOX_TOKEN } from '@/config';
import { useRegion } from '@/common/providers/region-provider';

const radius = 50;
const maxZoom = 20;
const minZoom = 1;
const minPoints = 2;
const extent = 512;
const nodeSize = 64;

export type DashboardMapProps = {
    users: MapUserLocation[];
};

export function DashboardMap({ users }: DashboardMapProps): ReactElement {
    const { resolvedRegion } = useRegion();
    const defaultBounds = getLocaleRegionBoundingBox(resolvedRegion);

    const mapRef = useRef<MapRef>(null);
    const superClusterRef = useRef<SuperCluster>(null);
    const prevClusterMarkersRef = useRef<unknown>(null);
    const [mapLoading, setMapLoading] = useState(true);
    const [markers, setMarkers] = useState<
        (
            | SuperCluster.PointFeature<SuperCluster.AnyProps>
            | SuperCluster.ClusterFeature<SuperCluster.AnyProps>
        )[]
    >([]);
    const [focused, setFocused] = useState(true);

    // Calculate markers
    const _calcMarkers = useRef(throttle(calcMarkers, 500, { trailing: true }));
    function calcMarkers() {
        if (mapRef.current && superClusterRef.current) {
            const zoom: number = mapRef.current.getZoom();
            const bounds = mapRef.current.getBounds();

            const topRightCoords = {
                lat: bounds?._ne.lat ?? 0,
                lng: bounds?._ne.lng ?? 0
            };
            const bottomLeftCoords = {
                lat: bounds?._sw.lat ?? 0,
                lng: bounds?._sw.lng ?? 0
            };
            const bBox: GeoJSON.BBox = [
                bottomLeftCoords.lng, // westLng - min lng
                bottomLeftCoords.lat, // southLat - min lat
                topRightCoords.lng, // eastLng - max lng
                topRightCoords.lat // northLat - max lat
            ];

            const _markers = superClusterRef.current?.getClusters(
                bBox,
                Math.floor(zoom)
            );
            if (!isEqual(prevClusterMarkersRef.current, _markers)) {
                setMarkers(_markers);
                prevClusterMarkersRef.current = _markers;
            }
        } else {
            console.error('Failed to get map ref or super cluster ref');
        }
    }

    // Update cluster
    useEffect(() => {
        if (mapLoading) {
            return;
        }

        const rawData: SuperCluster.PointFeature<SuperCluster.AnyProps>[] = [];
        users.forEach((user, index) => {
            rawData.push(markerToGeoJSONFeature(user, index));
        });

        superClusterRef.current = new SuperCluster({
            radius,
            maxZoom,
            minZoom,
            minPoints,
            extent,
            nodeSize
        });
        superClusterRef.current.load(rawData);

        calcMarkers();
    }, [users, mapLoading]);

    // Focus on all users
    useEffect(() => {
        if (mapLoading || !focused || users.length === 0) {
            return;
        }

        const bounds = increaseBoundsBy(locationsToBounds(users), 15);
        mapRef.current?.fitBounds(
            [bounds.sw.lon, bounds.sw.lat, bounds.ne.lon, bounds.ne.lat],
            { maxZoom: 17, animate: true }
        );
    }, [mapLoading, focused, users]);

    return (
        <div className={styles.container}>
            <Map
                ref={mapRef}
                initialViewState={{
                    bounds: [
                        defaultBounds.sw.lon,
                        defaultBounds.sw.lat,
                        defaultBounds.ne.lon,
                        defaultBounds.ne.lat
                    ]
                }}
                style={{ width: '100%', height: '100%' }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken={MAPBOX_TOKEN}
                onMove={_calcMarkers.current}
                onLoad={() => {
                    setMapLoading(false);
                }}
                onDragStart={() => setFocused(false)}
                onTouchMove={() => setFocused(false)}
                onZoomStart={(e) => {
                    if (
                        (e as unknown as { originalEvent: boolean })
                            .originalEvent
                    ) {
                        setFocused(false);
                    }
                }}
            >
                {markers
                    .sort(
                        (a, b) =>
                            b.geometry.coordinates[1] -
                            a.geometry.coordinates[1]
                    )
                    .map((marker) =>
                        !marker.properties.cluster ? (
                            <UserMapMarker
                                key={
                                    marker.geometry.coordinates[1] +
                                    marker.geometry.coordinates[0]
                                }
                                lat={marker.geometry.coordinates[1]}
                                lng={marker.geometry.coordinates[0]}
                                users={[marker.properties.user]}
                                // onPress={() =>
                                //     onMarkerPress([marker.properties.user])
                                // }
                            />
                        ) : (
                            (function () {
                                if (!superClusterRef.current) {
                                    return null;
                                }
                                let leaves;
                                try {
                                    leaves = superClusterRef.current.getLeaves(
                                        marker.id as number,
                                        Infinity
                                    );
                                } catch {
                                    console.error('Failed to get leaves');
                                    return null;
                                }
                                const users: MapUserLocation[] = [];
                                for (const leaf of leaves) {
                                    if (leaf.type === 'Feature') {
                                        users.push(
                                            leaf.properties
                                                .user as MapUserLocation
                                        );
                                    }
                                }

                                if (users.length > 0) {
                                    return (
                                        <UserMapMarker
                                            key={
                                                marker.geometry.coordinates[1] +
                                                marker.geometry.coordinates[0]
                                            }
                                            lat={marker.geometry.coordinates[1]}
                                            lng={marker.geometry.coordinates[0]}
                                            users={users}
                                            // onPress={() =>
                                            //    onMarkerPress(locations)
                                            // }
                                        />
                                    );
                                } else {
                                    return null;
                                }
                            })()
                        )
                    )}
            </Map>
            <div style={{ position: 'absolute', right: 16, bottom: 24 }}>
                <MapButton
                    onClick={() => {
                        setFocused((focused) => !focused);
                    }}
                    selected={focused}
                />
            </div>
        </div>
    );
}

const markerToGeoJSONFeature = (
    user: MapUserLocation,
    index: number
): SuperCluster.PointFeature<SuperCluster.AnyProps> => {
    return {
        type: 'Feature',
        geometry: {
            coordinates: [user.lng, user.lat],
            type: 'Point'
        },
        properties: {
            point_count: 0,
            index,
            user
        }
    };
};
